/**
 * Inline SVG "featured image" for pSEO pages. Text inside <title>, <desc>
 * and <text> elements is crawlable by Google. Each variant renders unique,
 * page-specific text so it doubles as a lightweight share illustration.
 */

import { cn } from "@/lib/utils";

type Variant =
  | "year" | "month" | "day"
  | "holiday" | "joint-leave" | "red-dates"
  | "hijri-year" | "hijri-month"
  | "tool" | "about" | "contact" | "legal"
  | "home";

/**
 * Theme keys for unique mini-illustrations stamped INSIDE the calendar
 * icon (replaces the generic day badge). Keep the SVG markup tiny so
 * the file remains lightweight.
 */
export type IconKey =
  | "kartini" | "guru" | "ibu" | "ayah"
  | "buruh" | "batik" | "pahlawan" | "kemerdekaan"
  | "valentine" | "natal" | "sumpah" | "anak"
  | "since-heart" | "since-cake" | "since-ring"
  | "since-broken" | "since-friends" | "since-ldr"
  | "since-clock";

interface Props {
  variant: Variant;
  title: string;
  subtitle?: string;
  /** Big numeric/textual badge inside the icon, e.g. year or day-of-month. */
  badge?: string;
  /** Optional themed pictogram drawn instead of the badge text. */
  iconKey?: IconKey;
  className?: string;
  /** "id" | "en" — purely affects accessibility text. */
  locale?: "id" | "en";
}

const PALETTES: Record<Variant, { from: string; to: string; ink: string }> = {
  year:        { from: "#fee2e2", to: "#fecaca", ink: "#991b1b" },
  month:       { from: "#dbeafe", to: "#bfdbfe", ink: "#1e3a8a" },
  day:         { from: "#dcfce7", to: "#bbf7d0", ink: "#166534" },
  holiday:     { from: "#fee2e2", to: "#fca5a5", ink: "#991b1b" },
  "joint-leave": { from: "#fef3c7", to: "#fde68a", ink: "#854d0e" },
  "red-dates": { from: "#fee2e2", to: "#fecaca", ink: "#991b1b" },
  "hijri-year":  { from: "#dcfce7", to: "#a7f3d0", ink: "#065f46" },
  "hijri-month": { from: "#d1fae5", to: "#a7f3d0", ink: "#065f46" },
  tool:        { from: "#ede9fe", to: "#ddd6fe", ink: "#5b21b6" },
  about:       { from: "#fae8ff", to: "#f5d0fe", ink: "#86198f" },
  contact:     { from: "#cffafe", to: "#a5f3fc", ink: "#155e75" },
  legal:       { from: "#e2e8f0", to: "#cbd5e1", ink: "#334155" },
  home:        { from: "#fee2e2", to: "#dbeafe", ink: "#0f172a" },
};

export function FeaturedSvg({ variant, title, subtitle, badge, iconKey, className, locale = "id" }: Props) {
  const pal = PALETTES[variant];
  const ariaLabel = subtitle ? `${title}, ${subtitle}` : title;
  const gid = `fsvg-${variant}-${Math.abs(hash(title + (subtitle ?? "") + (badge ?? "")))}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 480 200"
      role="img"
      aria-label={ariaLabel}
      className={cn("h-auto w-full max-w-md", className)}
      preserveAspectRatio="xMidYMid meet"
    >
      <title>{ariaLabel}</title>
      <desc>
        {locale === "en"
          ? `Featured image for ${title}${subtitle ? `, ${subtitle}` : ""}.`
          : `Gambar utama halaman ${title}${subtitle ? `, ${subtitle}` : ""}.`}
      </desc>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={pal.from} />
          <stop offset="100%" stopColor={pal.to} />
        </linearGradient>
      </defs>
      <rect width="480" height="200" rx="16" fill={`url(#${gid})`} />
      {/* Calendar icon */}
      <g transform="translate(28,38)">
        <rect x="0" y="10" width="120" height="116" rx="14" fill="#ffffff" stroke={pal.ink} strokeWidth="3" />
        <rect x="0" y="10" width="120" height="28" rx="14" fill={pal.ink} />
        <rect x="0" y="24" width="120" height="14" fill={pal.ink} />
        <rect x="22" y="0" width="10" height="22" rx="3" fill={pal.ink} />
        <rect x="88" y="0" width="10" height="22" rx="3" fill={pal.ink} />
        {iconKey ? (
          <g transform="translate(20,52)">{renderIcon(iconKey, pal.ink)}</g>
        ) : (
          <text
            x="60" y="92"
            textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fontSize="44"
            fill={pal.ink}
          >
            {badge ?? "31"}
          </text>
        )}
      </g>
      {/* Text block */}
      <g transform="translate(176,72)">
        <text
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontWeight="800"
          fontSize="28"
          fill={pal.ink}
        >
          {title}
        </text>
        {subtitle ? (
          <text
            y="28"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            fontWeight="500"
            fontSize="16"
            fill={pal.ink}
            opacity="0.85"
          >
            {subtitle}
          </text>
        ) : null}
        <text
          y="64"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontWeight="600"
          fontSize="13"
          fill={pal.ink}
          opacity="0.7"
        >
          {locale === "en" ? "Indonesian Calendar" : "Kalender Indonesia"}
        </text>
      </g>
    </svg>
  );
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

/** Tiny pictograms drawn into an 80x60 box, fill = ink. */
function renderIcon(key: IconKey, ink: string) {
  const p = (d: string, extra: Record<string, string | number> = {}) => (
    <path d={d} fill={ink} {...extra} />
  );
  switch (key) {
    case "kartini": // bun + kebaya silhouette
      return (
        <g>
          <circle cx="40" cy="20" r="12" fill={ink} />
          <circle cx="40" cy="10" r="6" fill={ink} />
          {p("M22 56 Q40 30 58 56 Z")}
        </g>
      );
    case "guru": // grad cap
      return (
        <g>
          {p("M10 30 L40 18 L70 30 L40 42 Z")}
          {p("M22 36 L22 50 Q40 58 58 50 L58 36 L40 44 Z")}
        </g>
      );
    case "ibu": // mother + child heart
      return (
        <g>
          {p("M40 10 C30 10 26 22 32 28 C26 30 22 38 22 50 L58 50 C58 38 54 30 48 28 C54 22 50 10 40 10 Z")}
          {p("M40 36 l4 4 l4 -4 a3 3 0 0 0 -4 -4 a3 3 0 0 0 -4 4 Z", { fill: "#ffffff" })}
        </g>
      );
    case "ayah": // tie
      return (
        <g>
          {p("M30 8 H50 L46 18 L54 56 L40 60 L26 56 L34 18 Z")}
        </g>
      );
    case "buruh": // wrench + hammer cross
      return (
        <g>
          {p("M14 50 L40 24 L50 34 L24 60 Z", { transform: "rotate(-15 30 40)" })}
          <circle cx="14" cy="50" r="6" fill={ink} />
          {p("M44 14 H62 V22 H44 Z")}
          {p("M50 22 H56 V58 H50 Z")}
        </g>
      );
    case "batik": // batik motif circles
      return (
        <g>
          <circle cx="20" cy="20" r="8" fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="60" cy="20" r="8" fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="20" cy="50" r="8" fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="60" cy="50" r="8" fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="40" cy="35" r="6" fill={ink} />
        </g>
      );
    case "pahlawan": // star + ribbon
      return (
        <g>
          {p("M40 6 L46 26 L66 26 L50 38 L56 58 L40 46 L24 58 L30 38 L14 26 L34 26 Z")}
        </g>
      );
    case "kemerdekaan": // flag
      return (
        <g>
          {p("M14 6 V60 H18 V36 H58 V8 H18 V6 Z")}
          {p("M18 8 H58 V20 H18 Z", { fill: "#dc2626" })}
        </g>
      );
    case "valentine": // heart
      return p("M40 58 C10 38 18 12 40 22 C62 12 70 38 40 58 Z");
    case "natal": // tree
      return (
        <g>
          {p("M40 6 L24 26 H32 L20 42 H30 L16 60 H64 L50 42 H60 L48 26 H56 Z")}
          {p("M36 60 H44 V64 H36 Z")}
        </g>
      );
    case "sumpah": // raised fist + scroll
      return (
        <g>
          {p("M22 22 H58 V40 Q40 56 22 40 Z")}
          {p("M28 14 H52 V24 H28 Z")}
        </g>
      );
    case "anak": // balloons
      return (
        <g>
          <circle cx="24" cy="22" r="12" fill={ink} />
          <circle cx="50" cy="18" r="10" fill={ink} />
          {p("M24 34 L24 60 M50 28 L50 60", { fill: "none", stroke: ink, strokeWidth: 2 } as never)}
        </g>
      );
    case "since-heart": return p("M40 58 C10 38 18 12 40 22 C62 12 70 38 40 58 Z");
    case "since-cake": // birthday cake
      return (
        <g>
          {p("M14 38 H66 V58 H14 Z")}
          {p("M14 38 H66 V44 H14 Z", { fill: "#ffffff" })}
          <circle cx="40" cy="14" r="4" fill={ink} />
          {p("M38 18 H42 V32 H38 Z")}
        </g>
      );
    case "since-ring":
      return (
        <g>
          <circle cx="40" cy="40" r="16" fill="none" stroke={ink} strokeWidth="6" />
          {p("M34 14 H46 L40 26 Z")}
        </g>
      );
    case "since-broken": // broken heart
      return (
        <g>
          {p("M40 22 C30 12 14 22 22 36 L38 56 L40 50 L36 42 L42 38 L38 32 L42 26 Z")}
          {p("M40 22 C50 12 66 22 58 36 L42 56 L40 50 L44 42 L38 38 L42 32 L38 26 Z")}
        </g>
      );
    case "since-friends":
      return (
        <g>
          <circle cx="26" cy="22" r="9" fill={ink} />
          <circle cx="54" cy="22" r="9" fill={ink} />
          {p("M10 56 Q26 36 42 56 Z")}
          {p("M38 56 Q54 36 70 56 Z")}
        </g>
      );
    case "since-ldr":
      return (
        <g>
          <circle cx="20" cy="40" r="10" fill={ink} />
          <circle cx="60" cy="40" r="10" fill={ink} />
          {p("M30 40 H50", { fill: "none", stroke: ink, strokeWidth: 3, strokeDasharray: "4 4" } as never)}
          {p("M40 30 l3 6 l-6 0 Z", { fill: "#ffffff" })}
        </g>
      );
    case "since-clock":
      return (
        <g>
          <circle cx="40" cy="36" r="22" fill="none" stroke={ink} strokeWidth="4" />
          {p("M40 36 L40 22 M40 36 L52 40", { fill: "none", stroke: ink, strokeWidth: 3 } as never)}
        </g>
      );
  }
}
